import DeleteNoteModal from "@components/notes/common/DeleteNoteModal";
import {
  faHighlighter,
  faStamp,
  faArrowRightArrowLeft,
  faTrash,
  faHistory,
  faEllipsisH,
  faFlag,
  faCheck,
  faSave,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFindAndReplace, usePreferences } from "@hooks";
import { useTeam } from "@hooks/use-team";
import {
  useTemplateHandler,
  useLegacyTemplateHandler,
} from "@hooks/use-template-handler";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useRef, useState } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { animated, config, useSpring } from "react-spring";

import AutoTemplatesModal from "../../../autotemplates/AutoTemplatesModal";
import { alert } from "../../../common/Alert";
import { TransferMultiModal } from "../../../common/multiselect/TransferMultiModal";
import SomethingWrongModal from "../../common/SomethingWrongModal";
import NoteFormTypeToggle from "../../legacy/LegacyNoteFormTypeToggle";

function ButtonGroupItem({
  label,
  icon,
  onClick,
  tooltipText,
  customClassName = null,
  dataTestId = null,
}) {
  return (
    <Tooltip title={tooltipText} arrow>
      <button
        className={`${
          customClassName
            ? customClassName
            : "bg-primary text-brand hover:bg-secondary"
        }  font-semibold justify-center px-4 h-[40px] text-xs text-nowrap flex flex-row items-center transition-all focus:outline-hidden`}
        onClick={onClick}
        data-testid={dataTestId}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`${label && "lg:mr-2"}`}
        />
        {label && <span className="hidden lg:block">{label}</span>}
      </button>
    </Tooltip>
  );
}

function MenuItem({
  onClick,
  title,
  icon = null,
  dataTestId = null,
  customClassName = null,
}) {
  return (
    <div
      className={`block px-4 py-2 text-sm cursor-pointer ${
        customClassName
          ? customClassName
          : "text-primary hover:bg-secondary"
      }`}
      role="menuitem"
      onClick={onClick}
      data-testid={dataTestId}
    >
      {icon && <FontAwesomeIcon icon={icon} className="mr-2" />}{" "}
      {title}
    </div>
  );
}

export default function NoteActionsToolbar({
  isAutoNote,
  note,
  refetchNote,
  onSaveAsDraft,
  onSubmitNote,
  isTitleFocused,
}) {
  const { control } = useFormContext();
  const navigate = useNavigate();
  const { currentTeam } = useTeam();
  const { useLegacyNotes } = useFlags();
  useFindAndReplace();

  const { isFindAndReplaceEnabled, setIsFindAndReplaceEnabled } =
    usePreferences();
  const [isTransferModalOpen, setIsTransferModalOpen] =
    useState(false);
  const [shouldShowMenu, setShouldShowMenu] = useState(false);
  const [shouldShowFlagNoteModal, setShouldShowFlagNoteModal] =
    useState(false);
  const [shouldShowDeleteNoteModal, setShouldShowDeleteNoteModal] =
    useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const checkIfAtBottom = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const isBottom =
        scrollTop + windowHeight >= documentHeight - 200;
      setIsAtBottom(isBottom);
    };

    window.addEventListener("scroll", checkIfAtBottom);
    window.addEventListener("resize", checkIfAtBottom);

    checkIfAtBottom();

    return () => {
      window.removeEventListener("scroll", checkIfAtBottom);
      window.removeEventListener("resize", checkIfAtBottom);
    };
  }, []);

  const shouldUseCustomForm = useWatch({
    control,
    name: "shouldUseCustomForm",
  });

  const menuAnim = useSpring({
    config: config.wobbly,
    opacity: shouldShowMenu ? 1 : 0,
    transform: shouldShowMenu ? "scale(1)" : "scale(0.8)",
  });

  // Get template handler functions
  const {
    isModalOpen,
    handleTemplateSelect,
    openTemplateModal,
    closeTemplateModal,
  } = useTemplateHandler(note);

  const { handleTemplateClick } = useLegacyTemplateHandler(note);

  // Use shared handlers
  const onFindAndReplaceToggle = () => {
    if (!isFindAndReplaceEnabled) {
      alert("success", "Highlight a word with your mouse to begin");
    }
    setIsFindAndReplaceEnabled(!isFindAndReplaceEnabled);
  };

  const onTransferNote = () => setIsTransferModalOpen(true);

  const onFlagNote = () => {
    setShouldShowFlagNoteModal(true);
  };

  const onDeleteNote = () => setShouldShowDeleteNoteModal(true);

  const onMoreClick = () => setShouldShowMenu(!shouldShowMenu);

  const onNoteHistory = () => {
    navigate(`/audit_trail/note/${note?.uuid}`);
  };

  /**
   * Hook that alerts clicks outside of the passed ref
   */
  function useOutsideAlerter(ref) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShouldShowMenu(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  return (
    <>
      <AutoTemplatesModal
        isOpen={isModalOpen}
        onClose={closeTemplateModal}
        onTemplateSelect={handleTemplateSelect}
        isReprocessingMode={true}
      />

      <TransferMultiModal
        notes={[note]}
        shouldShowTransferModal={isTransferModalOpen}
        setShouldShowTransferModal={setIsTransferModalOpen}
        refetch={refetchNote}
        bypassContext={true}
      />

      <SomethingWrongModal
        note={note}
        refetchNote={refetchNote}
        shouldShowFlagNoteModal={shouldShowFlagNoteModal}
        setShouldShowFlagNoteModal={setShouldShowFlagNoteModal}
      />

      <DeleteNoteModal
        note={note}
        shouldShowDeleteNoteModal={shouldShowDeleteNoteModal}
        setShouldShowDeleteNoteModal={setShouldShowDeleteNoteModal}
      />

      <div
        className={`${
          !isTitleFocused && "divide-x divide-primary"
        } relative flex flex-row items-center border border-primary rounded-lg shadow-xs`}
      >
        <div
          className={`${
            isTitleFocused
              ? "hidden"
              : "hidden md:flex divide-x divide-primary"
          } rounded-lg`}
        >
          <ButtonGroupItem
            label="Save"
            icon={faSave}
            onClick={onSaveAsDraft}
            tooltipText="Click to save the note as a draft. This will save all edits to note history and close the note editor."
            dataTestId="save-draft-button"
            customClassName={`${
              isAtBottom
                ? "bg-indigo-500 text-white hover:bg-indigo-600"
                : "bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800 dark:hover:text-indigo-400"
            } rounded-l-lg`}
          />
          {!(note?.isTagged && note?.isApproved) && (
            <ButtonGroupItem
              label="Mark as Done"
              icon={faCheck}
              onClick={onSubmitNote}
              tooltipText="Click to mark the note as done. This will save the note and move it to the 'Ready to Export' section of the Inbox."
              dataTestId="submit-note-button"
              customClassName={`${
                isAtBottom
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800 dark:hover:text-green-400"
              }`}
            />
          )}
          <ButtonGroupItem
            label="Templates"
            icon={faStamp}
            onClick={
              isAutoNote || !useLegacyNotes
                ? openTemplateModal
                : handleTemplateClick
            }
            tooltipText="Click to use a different template. This will reprocess the note with the selected template and clear any edits you've made."
            dataTestId={
              isAutoNote || !useLegacyNotes
                ? "new-use-template-button"
                : "legacy-use-template-button"
            }
            customClassName="bg-primary text-brand hover:bg-secondary"
          />
        </div>

        {/* Hide the form toggle for now ONLY FOR TYPED NOTES*/}
        {isAutoNote || !useLegacyNotes ? null : (
          <Controller
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange } }) => (
              <NoteFormTypeToggle
                onChange={onChange}
                value={shouldUseCustomForm}
              />
            )}
            name="shouldUseCustomForm"
          />
        )}
        <div ref={wrapperRef}>
          <ButtonGroupItem
            label="More"
            icon={faEllipsisH}
            onClick={onMoreClick}
            dataTestId="more-button"
            customClassName={`${
              !isTitleFocused && "md:rounded-r-lg md:rounded-l-none"
            } rounded-lg bg-primary text-brand hover:bg-secondary`}
          />
        </div>

        <animated.div
          style={menuAnim}
          ref={wrapperRef}
          className={`absolute top-10 right-0 bg-primary border border-primary rounded-lg shadow-lg z-50 divide-y divide-primary min-w-[200px] ${
            shouldShowMenu ? "block" : "hidden"
          }`}
        >
          <div
            className={`flex flex-col  ${
              isTitleFocused ? "block" : "md:hidden"
            }`}
          >
            <MenuItem
              title="Save and Close"
              icon={faSave}
              onClick={onSaveAsDraft}
              tooltipText="Click to save the note as a draft. This will save all edits to note history andclose the note editor."
              dataTestId="save-draft-button"
              customClassName="text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/50"
            />
            {!(note?.isTagged && note?.isApproved) && (
              <MenuItem
                title="Mark as Done"
                icon={faCheck}
                onClick={onSubmitNote}
                tooltipText="Click to mark the note as done. This will save the note and move it to the 'Ready to Export' section of the Inbox."
                dataTestId="submit-note-button"
                customClassName="text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/50"
              />
            )}
          </div>

          <div className="flex flex-col md:hidden">
            <MenuItem
              title="Change Template"
              icon={faStamp}
              onClick={
                isAutoNote ? openTemplateModal : handleTemplateClick
              }
              tooltipText="Click to use a different template. This will reprocess the note with the selected template and clear any edits you've made."
              dataTestId={
                isAutoNote
                  ? "new-use-template-button"
                  : "legacy-use-template-button"
              }
            />
          </div>
          <div>
            <MenuItem
              title={
                isFindAndReplaceEnabled
                  ? "Select/Replace: ON"
                  : "Select/Replace: OFF"
              }
              icon={faHighlighter}
              onClick={onFindAndReplaceToggle}
              isEnabled={isFindAndReplaceEnabled}
              tooltipText="Click to use Select and Replace. This will allow you to highlight a word and replace all instances of it at once."
              dataTestId="select-replace-button"
            />
            {note?.user && !note?.patient && currentTeam?.uuid && (
              <MenuItem
                title="Transfer Note"
                icon={faArrowRightArrowLeft}
                onClick={onTransferNote}
                tooltipText="Click to transfer the note to a different team member."
                dataTestId="transfer-button"
              />
            )}
            <MenuItem
              onClick={onNoteHistory}
              title="Note History"
              dataTestId="history-button"
              icon={faHistory}
            />
            <MenuItem
              title="Flag Note"
              icon={faFlag}
              onClick={onFlagNote}
              dataTestId="something-wrong-button"
            />
          </div>
          <MenuItem
            title="Delete Note"
            icon={faTrash}
            onClick={onDeleteNote}
            customClassName={
              "text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50"
            }
          />
        </animated.div>
      </div>
    </>
  );
}
